// src/components/modals/ViewInventoryModal.jsx
import { X, Package, Building, Calendar, DollarSign, AlertTriangle } from 'lucide-react'
import { Button } from '../ui/Button'

const ViewInventoryModal = ({ isOpen, onClose, inventory }) => {
  // Don't render if not open or no inventory
  if (!isOpen || !inventory) return null

  const material = inventory.rawMaterial
  const branch = inventory.branch
  const unit = material?.unit

  // Calculate stock status
  const getStockStatus = (currentStock, threshold) => {
    if (currentStock === 0) {
      return { status: 'out_of_stock', label: 'Out of Stock', color: 'text-red-600' }
    } else if (currentStock <= threshold) {
      return { status: 'low_stock', label: 'Low Stock', color: 'text-yellow-600' }
    } else if (currentStock > threshold * 3) {
      return { status: 'over_stock', label: 'Over Stock', color: 'text-green-600' }
    } else {
      return { status: 'normal', label: 'Normal', color: 'text-blue-600' }
    }
  }

  const stockInfo = getStockStatus(inventory.currentStock, material?.threshold || 0)
  const stockValue = (inventory.currentStock || 0) * (inventory.unitPrice || 0)

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Inventory Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Material Information */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Package className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Material Information</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Material Name
                </label>
                <p className="text-gray-900 font-medium">{material?.name || 'Unknown Material'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Material Code
                </label>
                <p className="text-gray-900 font-mono text-sm bg-gray-100 px-2 py-1 rounded inline-block">
                  {material?.code || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Unit
                </label>
                <p className="text-gray-900">
                  {unit ? `${unit.name} (${unit.abbreviation})` : 'Unknown Unit'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Threshold
                </label>
                <p className="text-gray-900">
                  {material?.threshold || 0} {unit?.abbreviation || ''}
                </p>
              </div>
            </div>
            {material?.description && (
              <div className="mt-3">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Description
                </label>
                <p className="text-gray-900 text-sm">{material.description}</p>
              </div>
            )}
          </div>

          {/* Branch Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Building className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-900">Branch Information</h3>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-sm font-medium text-blue-700 block mb-1">
                  Branch Name
                </label>
                <p className="text-blue-900 font-medium">{branch?.name || 'Unknown Branch'}</p>
              </div>
              {branch?.address && (
                <div>
                  <label className="text-sm font-medium text-blue-700 block mb-1">
                    Address
                  </label>
                  <p className="text-blue-900 text-sm">{branch.address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Inventory Details */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Current Stock
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {inventory.currentStock || 0}
                  </span>
                  <span className="text-gray-600">{unit?.abbreviation || ''}</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Stock Status
                </label>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    stockInfo.status === 'normal' ? 'bg-blue-100 text-blue-800' :
                    stockInfo.status === 'low_stock' ? 'bg-yellow-100 text-yellow-800' :
                    stockInfo.status === 'out_of_stock' ? 'bg-red-100 text-red-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {stockInfo.label}
                  </span>
                  {stockInfo.status !== 'normal' && stockInfo.status !== 'over_stock' && (
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  )}
                </div>
              </div>
              {inventory.unitPrice && (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      Unit Price
                    </label>
                    <p className="text-gray-900 font-medium">
                      Rs. {Number(inventory.unitPrice).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      Total Value
                    </label>
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      <span className="text-lg font-bold text-green-600">
                        Rs. {stockValue.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </>
              )}
              {/* <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Status
                </label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  inventory.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {inventory.isActive ? 'Active' : 'Inactive'}
                </span>
              </div> */}
            </div>
          </div>

          {/* Stock Analysis */}
          {material?.threshold && (
            <div className={`border rounded-lg p-4 ${
              stockInfo.status === 'out_of_stock' ? 'bg-red-50 border-red-200' :
              stockInfo.status === 'low_stock' ? 'bg-yellow-50 border-yellow-200' :
              'bg-green-50 border-green-200'
            }`}>
              <h4 className={`text-sm font-medium mb-2 ${
                stockInfo.status === 'out_of_stock' ? 'text-red-900' :
                stockInfo.status === 'low_stock' ? 'text-yellow-900' :
                'text-green-900'
              }`}>
                Stock Analysis
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Stock:</span>
                  <span className="font-medium">{inventory.currentStock || 0} {unit?.abbreviation || ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Threshold:</span>
                  <span className="font-medium">{material.threshold} {unit?.abbreviation || ''}</span>
                </div>
                {/* <div className="flex justify-between">
                  <span className="text-gray-600">Days Above Threshold:</span>
                  <span className="font-medium">
                    {inventory.currentStock > material.threshold 
                      ? Math.round((inventory.currentStock - material.threshold) / (material.threshold * 0.1)) 
                      : 0
                    } days*
                  </span>
                </div> */}
              </div>
              {/* <p className="text-xs text-gray-500 mt-2">
                *Estimated based on 10% daily usage rate
              </p> */}
            </div>
          )}

          {/* Metadata */}
          {(inventory.createdAt || inventory.updatedAt || inventory.lastUpdated) && (
            <div className="border-t pt-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="h-4 w-4 text-gray-600" />
                <h4 className="text-sm font-medium text-gray-700">Timeline</h4>
              </div>
              <div className="space-y-2 text-sm">
                {inventory.createdAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-900">{formatDate(inventory.createdAt)}</span>
                  </div>
                )}
                {inventory.lastUpdated && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Stock Update:</span>
                    <span className="text-gray-900">{formatDate(inventory.lastUpdated)}</span>
                  </div>
                )}
                {inventory.updatedAt && inventory.updatedAt !== inventory.createdAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Modified:</span>
                    <span className="text-gray-900">{formatDate(inventory.updatedAt)}</span>
                  </div>
                )}
                {inventory._id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Inventory ID:</span>
                    <span className="text-gray-900 font-mono text-xs">{inventory._id}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
          <div className="flex justify-end pt-6 pr-6 pb-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
      </div>
    </div>
  )
}

export default ViewInventoryModal